import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, endpoints } from '../config/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load tokens from localStorage on mount
  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    
    if (accessToken && refreshToken) {
      setTokens({ access: accessToken, refresh: refreshToken });
      fetchCurrentUser(accessToken);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchCurrentUser = async (token) => {
    try {
      const response = await api.get(endpoints.auth.me, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response);  // response is already the data
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Called by SsoCallbackPage once a Microsoft sign-in's exchange code has
  // been traded for tokens (there is no password login — see LoginPage).
  const setSession = (userData, userTokens) => {
    setUser(userData);
    setTokens(userTokens);
    localStorage.setItem('access_token', userTokens.access);
    localStorage.setItem('refresh_token', userTokens.refresh);
  };

  const logout = async () => {
    try {
      if (tokens?.refresh) {
        await api.post(endpoints.auth.logout, { refresh: tokens.refresh });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setTokens(null);
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  };

  const refreshAccessToken = async () => {
    try {
      const response = await api.post(endpoints.auth.refresh, { 
        refresh: tokens.refresh 
      });
      const newAccessToken = response.access;  // response is already the data
      
      setTokens(prev => ({ ...prev, access: newAccessToken }));
      localStorage.setItem('access_token', newAccessToken);
      
      return newAccessToken;
    } catch (error) {
      logout();
      return null;
    }
  };

  const value = {
    user,
    tokens,
    loading,
    setSession,
    logout,
    refreshAccessToken,
    fetchCurrentUser,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === 'super_admin',
    isOrgAdmin: user?.role === 'org_admin',
    isOrgUser: user?.role === 'org_user',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
